import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { NzTableFilterList } from 'ng-zorro-antd/table';
import { actions, selectors } from '@twaice-fe/frontend/shared/store';
import {
  PackageFeature,
  ProductPackageService,
  StorageLevelToCustomerLevelNamePipe,
  SystemMetadataService,
} from '@twaice-fe/frontend/shared/services';
import {
  FeatureFlagsEnum,
  HealthSummary,
  PerformanceState,
  PerformanceSummary,
  ProductPackages,
  RiskSummary,
  RTESummary,
  SafetySummary,
  SpreadState,
  StorageLevel,
  SystemDetails,
  WarrantyState,
} from '@twaice-fe/shared/models';
import { isEqual } from '@twaice-fe/shared/utilities';
import { combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';
import {
  ANOMALIES_COLUMN,
  ColumnItem,
  ColumnVisibility,
  featureNotAvailable,
  onBoardingInProgress,
  PENALTY_RISK_COLUMN,
  PERFORMANCE_STATUS_COLUMN,
  RTE_SUMMARY_COLUMN,
  StatsValuesConfigInterface,
  STATUS_CONFIGS,
  SystemOverviewAntTableInterface,
  SystemPortfolioOverviewViewModel,
  WARRANTY_STATUS_COLUMN,
} from './system-portfolio-overview-table-config';
import {
  filterByStatusLabel,
  sortByNumber,
  sortByStatusSeverity,
  statusFilterOptions,
  StatusColumnKey,
} from './system-portfolio-overview-table-sorting';

const { systemSelectors, configsSelectors } = selectors;
const { systemActions } = actions;

@Injectable()
export class SystemPortfolioOverviewTableService {
  readonly viewModel$: Observable<SystemPortfolioOverviewViewModel>;

  private readonly filterOptionsCache = new Map<StatusColumnKey, NzTableFilterList>();

  constructor(
    private store: Store,
    private router: Router,
    private productPackageService: ProductPackageService,
    private storageLevelToLevelName: StorageLevelToCustomerLevelNamePipe,
    private systemMetadataService: SystemMetadataService
  ) {
    this.viewModel$ = this.createViewModel();
  }

  /**
   * Selects the system, then routes to the requested link scoped to that system.
   */
  navigateToSystem(data: SystemOverviewAntTableInterface, link: string): void {
    if (!link) return;

    this.store.dispatch(systemActions.selectSystem({ systemId: data.id }));
    this.router.navigateByUrl(this.buildRowUrl(data, link));
  }

  getStatus<T extends keyof typeof STATUS_CONFIGS>({
    category,
    stateKey,
    value,
    link,
  }: {
    category: T;
    stateKey: keyof (typeof STATUS_CONFIGS)[T] | null | undefined;
    value?: number | null;
    link: string;
  }): StatsValuesConfigInterface {
    if (!stateKey) {
      return { ...onBoardingInProgress, link };
    }

    return {
      ...STATUS_CONFIGS[category][stateKey],
      value: value !== undefined && stateKey !== SpreadState.STABLE ? Math.round(value * 10) / 10 : null,
      link,
    };
  }

  getSafetyScoreStatus(safetySummary: SafetySummary | null): StatsValuesConfigInterface {
    return this.getStatus({
      category: 'safety',
      stateKey: safetySummary?.safetyState,
      link: 'safety',
    });
  }

  getPerformanceStatus(performanceSummary: PerformanceSummary | null): StatsValuesConfigInterface {
    return this.getStatus({
      category: 'performance',
      stateKey: performanceSummary?.usableEnergyState,
      value: null,
      link: `/performance-manager/availability`,
    });
  }

  getHealthStatus(healthSummary: HealthSummary | null): StatsValuesConfigInterface {
    return this.getStatus({
      category: 'health',
      stateKey: healthSummary?.soh?.spreadState,
      value: healthSummary?.soh?.spreadState !== SpreadState.STABLE ? healthSummary?.soh?.spread : null,
      link: '/energy/health',
    });
  }

  getRteStatus(rteSummary: RTESummary | null): StatsValuesConfigInterface {
    return this.getStatus({
      category: 'rte',
      stateKey: rteSummary?.rteState,
      value: rteSummary?.rteState !== PerformanceState.HIGH ? rteSummary?.rtePercentage : null,
      link: '/warranty-tracker',
    });
  }

  getRiskStatus(riskSummary: RiskSummary | null): StatsValuesConfigInterface {
    return this.getStatus({
      category: 'risk',
      stateKey: riskSummary?.riskState,
      link: '/penalty-risk',
    });
  }

  getWarrantyStatus(warrantyState: WarrantyState | null): StatsValuesConfigInterface {
    return this.getStatus({
      category: 'warrantyState',
      stateKey: warrantyState,
      link: '/warranty-tracker',
    });
  }

  /** nz-table clears the ticked filters whenever the `nzFilters` array changes identity. */
  private memoizedFilterOptions(rows: SystemOverviewAntTableInterface[], key: StatusColumnKey): NzTableFilterList {
    const next = statusFilterOptions(rows, key);
    const cached = this.filterOptionsCache.get(key);
    if (cached && isEqual(cached, next)) return cached;
    this.filterOptionsCache.set(key, next);
    return next;
  }

  private buildRowUrl({ customerBk, systemBk }: SystemOverviewAntTableInterface, link: string): string {
    return `/customer/${customerBk}/system/${systemBk}/${link.replace(/^\/+/, '')}`;
  }

  private createViewModel(): Observable<SystemPortfolioOverviewViewModel> {
    const featureFlags$ = this.store
      .select(configsSelectors.getConfigFeatureFlagList)
      .pipe(distinctUntilChanged(isEqual)) as Observable<FeatureFlagsEnum[]>;

    return combineLatest({
      featureFlags: featureFlags$,
      productPackages: this.store.select(configsSelectors.getProductPackages),
      systemDetailsList: this.store.select(systemSelectors.getSystemDetailsList),
    }).pipe(
      map(({ featureFlags, productPackages, systemDetailsList }) => {
        const visibility: ColumnVisibility = {
          isRTESummaryEnabled: featureFlags.includes(FeatureFlagsEnum.SYSTEM_LEVEL_RTE),
          isPenaltyRiskEnabled: !featureFlags.includes(FeatureFlagsEnum.HIDE_PENALTY_RISK),
        };

        const rows = this.formatSystemDetailsData(systemDetailsList, productPackages, visibility);

        return {
          ...visibility,
          columns: this.createSystemOverviewColumnItems(visibility, rows),
          rows,
          isFinancialImpactDisabled: [
            FeatureFlagsEnum.ENERGY_PERFORMANCE_MANAGER_DISABLE_FINANCIAL_IMPACT,
            FeatureFlagsEnum.MODO_ENERGY_INTEGRATION,
          ].some((featureFlag) => featureFlags.includes(featureFlag)),
        };
      })
    );
  }

  private createSystemOverviewColumnItems(
    { isRTESummaryEnabled, isPenaltyRiskEnabled }: ColumnVisibility,
    rows: SystemOverviewAntTableInterface[]
  ): ColumnItem<SystemOverviewAntTableInterface>[] {
    const statusColumn = (column: ColumnItem<SystemOverviewAntTableInterface>, key: StatusColumnKey) => ({
      ...column,
      sortFn: sortByStatusSeverity(key),
      filters: this.memoizedFilterOptions(rows, key),
      filterFn: filterByStatusLabel(key),
    });

    return [
      {
        name: this.storageLevelToLevelName.transform(StorageLevel.System, true, 1),
        width: '223px',
        sortFn: (a: SystemOverviewAntTableInterface, b: SystemOverviewAntTableInterface) =>
          (a.systemName ?? '').localeCompare(b.systemName ?? ''),
      },
      {
        // Sorts on the percentage the cell prints, not on the three bands it colours.
        ...statusColumn(PERFORMANCE_STATUS_COLUMN, 'performance'),
        sortFn: sortByNumber((row) => row.usableEnergyPercentage),
      },
      statusColumn(ANOMALIES_COLUMN, 'safetyScore'),
      isPenaltyRiskEnabled ? statusColumn(PENALTY_RISK_COLUMN, 'risk') : null,
      statusColumn(WARRANTY_STATUS_COLUMN, 'warrantyState'),
      isRTESummaryEnabled ? statusColumn(RTE_SUMMARY_COLUMN, 'rte') : null,
    ].filter(Boolean);
  }

  private formatSystemDetailsData(
    systemDetailsList: SystemDetails[],
    productPackages: ProductPackages,
    { isRTESummaryEnabled, isPenaltyRiskEnabled }: ColumnVisibility
  ): SystemOverviewAntTableInterface[] {
    return systemDetailsList.map((systemDetails) => {
      const includes = (feature: PackageFeature) =>
        this.productPackageService.packageIncludes(productPackages[systemDetails.systemBk], feature);

      const risk = includes(PackageFeature.PenaltyRisk) ? this.getRiskStatus(systemDetails.riskSummary) : featureNotAvailable;

      return {
        customerBk: systemDetails.customerBk,
        systemBk: systemDetails.systemBk,
        id: systemDetails.id,
        systemName: systemDetails.systemName,
        bessMetadata: systemDetails.bessMetadata,
        storageLocation: this.systemMetadataService.getStorageLocation(systemDetails.bessMetadata),
        safetyScore: includes(PackageFeature.Anomalies)
          ? this.getSafetyScoreStatus(systemDetails.safetySummary)
          : featureNotAvailable,
        performance: includes(PackageFeature.RecoverableEnergy)
          ? this.getPerformanceStatus(systemDetails.performanceSummary)
          : featureNotAvailable,
        rte: isRTESummaryEnabled ? this.getRteStatus(systemDetails.performanceSummary?.rteSummary) : null,
        risk: isPenaltyRiskEnabled ? risk : null,
        warrantyState: this.getWarrantyStatus(systemDetails.warrantyState),
        usableEnergyPercentage: systemDetails.performanceSummary?.usableEnergyPercentage,
        pricePerMwh: systemDetails.pricePerMwh,
        recoverableEnergyMWh: systemDetails.performanceSummary?.recoverableEnergyWh / 1000000,
        newChatMessagesAvailable: systemDetails.newChatMessagesAvailable,
      };
    });
  }
}
